<template>
    <div class="vp-top">
        <div class="vp-top-text">
            <div class="vp-label vp-top-label">{{ showLabel }}</div>
            <div class="vp-heading vp-top-title">{{ title }}</div>
        </div>

        <div class="vp-top-actions">
            <a v-if="brandingVisible" href="https://www.springcast.io" target="_blank" rel="noopener" class="vp-top-logo">
                <SpringcastMark :size="30" />
            </a>

            <div v-if="switchVisible" class="vp-switch" role="group">
                <button type="button" :aria-pressed="mode === 'video'" :aria-label="t('Switch to video')" @click="emit('switch-mode', 'video')">
                    {{ t("Video") }}
                </button>
                <button type="button" :aria-pressed="mode === 'audio'" :aria-label="t('Switch to audio')" @click="emit('switch-mode', 'audio')">
                    {{ t("Audio") }}
                </button>
            </div>

            <button
                v-if="transcriptAvailable"
                type="button"
                class="vp-icon-button"
                :aria-label="transcriptOpen ? t('Close transcript') : t('Open transcript')"
                :aria-pressed="transcriptOpen"
                @click="emit('toggle-transcript')"
            >
                <VideoIcon name="transcript" />
            </button>

            <slot name="actions" />
        </div>
    </div>
</template>

<script setup>
import SpringcastMark from "./SpringcastMark.vue"
import VideoIcon from "./VideoIcon.vue"
import { useCoreI18n } from "../useCoreI18n.js"

defineProps({
    showLabel: { type: String, default: "" },
    title: { type: String, default: "" },
    brandingVisible: { type: Boolean, default: false },
    transcriptAvailable: { type: Boolean, default: false },
    transcriptOpen: { type: Boolean, default: false },
    switchVisible: { type: Boolean, default: false },
    mode: { type: String, default: "video" },
})

const emit = defineEmits(["toggle-transcript", "switch-mode"])

const t = useCoreI18n()
</script>

<style>
.vp-top {
    position: relative;
    z-index: 1;
    align-self: stretch;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 22px 24px 48px;
    background: linear-gradient(180deg, rgba(21, 18, 13, 0.7), rgba(21, 18, 13, 0));
}

.vp-top-text {
    min-width: 0;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.vp-top-title {
    overflow: hidden;
    font-size: 19px;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.vp-top-actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
}

.vp-top-logo {
    margin-right: 10px;
    color: var(--vp-paper);
    opacity: 0.86;
}

.vp-switch {
    height: 32px;
    margin-right: 8px;
    padding: 2px;
    display: inline-flex;
    border: 1px solid rgba(251, 249, 245, 0.24);
    border-radius: 999px;
}

.vp-switch button {
    position: relative;
    min-width: 44px;
    padding: 0 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: var(--vp-muted);
    background: transparent;
}

/* The pill stays 32px tall; the tap target extends to 44px like .vp-icon-button. */
.vp-switch button::before {
    content: "";
    position: absolute;
    inset: -9px 0;
}

.vp-switch button[aria-pressed="true"] {
    color: var(--vp-paper);
    background: rgba(251, 249, 245, 0.14);
}

.vp-top-feedback {
    font-size: 12px;
    color: var(--vp-muted);
}

media-controller:not([breakpointmd]) .vp-top-logo {
    display: none;
}
</style>
